import { Injectable, NgZone, inject } from "@angular/core";
import {
  ApiCommandResource,
  ApiQueryResource,
  Asr,
  IOrganizationLanguage,
  IProviderLanguages,
  ReconnectionReason,
} from "@auvious/asr";
import { AuviousCommon, PagedCollection } from "@auvious/common";
import { filter, Observable, Subject } from "rxjs";
import {
  ApplicationProviderTypeEnum,
  StreamTrackKindEnum,
  TranscriptEvent,
  TranscriptTransformType,
} from "../core-ui.enums";
import {
  AgentParam,
  IApplicationConfig,
  IProviderLanguage,
  ProviderOrganizationLanguage,
} from "../models";
import {
  ISpeechToTextTranscriptChunk,
  ITranscript,
  ITranscriptMessage,
  ITranscriptStatus,
  ITranscriptTransformStatus,
  ITranscriptTranslateStatus,
  ITransformContent,
} from "../models/interfaces";
import {
  ElevenLabsTranscriptStrategy,
  GoogleTranscriptStrategy,
  OpenAITranscriptStrategy,
} from "../models/strategies";
import {
  AIModelUsage,
  IProviderModel,
  IProviderModelDefault,
  IProvisionedModel,
  IProvisionLanguageOptions,
  ITranscriptStrategy,
  ITranscriptTransformOptions,
  ITranscriptTransformStrategy,
  ITranscriptTranslateOptions,
  ITranscriptTranslateStrategy,
  LiveToolDefinition,
  SpeechLanguage,
} from "../models/strategies/ITranscriptStrategy";
import { AppConfigService } from "./app.config.service";
import { ApplicationService } from "./application.service";
import { ConferenceService } from "./conference.service";
import { GenericErrorHandler } from "./error-handlers.service";
import { LocalMediaService } from "./local.media.service";
import { NotificationService } from "./notification.service";
import { AuviousRtcService } from "./rtc.service";
import { UserService } from "./user.service";

export interface ITranscriptEventHandlers {
  ready?: () => void;
  reconnecting?: (payload: {
    delay: number;
    times: number;
    reason: ReconnectionReason;
  }) => void;
  reconnected?: () => void;
  transcript?: (transcript: {
    userId: string;
    transcript: string;
    isFinal: boolean;
  }) => void;
  transcriptFailed?: () => void;
  translationFailed?: () => void;
}

interface ITranscriptService
  extends ITranscriptStrategy,
    ITranscriptTransformStrategy,
    ITranscriptTranslateStrategy {}

@Injectable()
export class TranscriptService implements ITranscriptService {
  private rtc = inject(AuviousRtcService);
  private local = inject(LocalMediaService);
  private conference = inject(ConferenceService);
  private notification = inject(NotificationService);
  private logger = inject(GenericErrorHandler);
  private zone = inject(NgZone);
  private user = inject(UserService);
  private application = inject(ApplicationService);
  private config = inject(AppConfigService);

  private apiCommand: ApiCommandResource;
  private apiQuery: ApiQueryResource;
  private instance: Asr;
  private common: AuviousCommon;
  private impl: ITranscriptService;

  private asrOfflineEventSubject: Subject<{
    id: string;
    type: TranscriptEvent;
  }> = new Subject();

  private _chunk = new Subject<ISpeechToTextTranscriptChunk>();
  public chunk$ = this._chunk.asObservable();

  private _reset = new Subject<void>();
  public reset$ = this._reset.asObservable();

  private _enabled = new Subject<void>();
  public enabled$ = this._enabled.asObservable();

  private _disabled = new Subject<void>();
  public disabled$ = this._disabled.asObservable();

  constructor() {
    const rtc = this.rtc;
    const config = this.config;

    rtc.common$.subscribe((c) => {
      this.apiCommand = new ApiCommandResource(c);
      this.apiQuery = new ApiQueryResource(c);
      this.common = c;
    });

    config.configChanged$.subscribe((c) => {
      this.providerChanged(this.getConfiguredProvider(c));
    });

    this.local.streamMutedChange$
      .pipe(
        filter(
          (s) => s.trackKind === StreamTrackKindEnum.audio && this.isEnabled
        )
      )
      .subscribe(async (s) => {
        try {
          await this.setStream(
            s.muted || this.conference.isConferenceOnHold
              ? null
              : s.stream.mediaStream
          );
        } catch (ex) {
          this.logger.handleError(ex);
          this.notification.error("Closed Captions", {
            body: "Could not start captions service",
          });
        }
      });
  }

  public get isLegacyGoogleProvider(): boolean {
    if (this.config.agentParam(AgentParam.TRANSCRIPT_PROVIDER)) {
      return false;
    } else {
      // backwards compatibility to when we had only google
      if (this.config.agentParam(AgentParam.TRANSCRIPT_PROVIDER_CONFIGURED)) {
        return true;
      }
      return false;
    }
  }

  public getConfiguredProvider(
    config: IApplicationConfig
  ): ApplicationProviderTypeEnum {
    let provider;
    if (this.user.isAdmin) {
      provider =
        config.serviceParameters?.transcriptProvider ||
        // backwards compatibility to when we had only google
        config.serviceParameters?.speechToTextStorageProvider;
    } else if (this.user.isAgent) {
      if (config.agentParameters?.[AgentParam.TRANSCRIPT_PROVIDER]) {
        provider = config.agentParameters[AgentParam.TRANSCRIPT_PROVIDER];
      } else {
        // backwards compatibility to when we had only google
        if (
          config.agentParameters?.[AgentParam.TRANSCRIPT_PROVIDER_CONFIGURED]
        ) {
          provider = ApplicationProviderTypeEnum.GOOGLE_CLOUD;
        }
      }
    }
    return provider;
  }

  public providerChanged(provider: ApplicationProviderTypeEnum) {
    switch (provider) {
      case ApplicationProviderTypeEnum.OPEN_AI:
        this.impl = new OpenAITranscriptStrategy(
          this.logger,
          this.rtc,
          this.application
        );
        break;
      case ApplicationProviderTypeEnum.ELEVENLABS:
        this.impl = new ElevenLabsTranscriptStrategy(
          this.logger,
          this.rtc,
          this.application
        );
        break;
      case ApplicationProviderTypeEnum.GOOGLE_AI_STUDIO:
      case ApplicationProviderTypeEnum.GOOGLE_CLOUD:
      default:
        this.impl = new GoogleTranscriptStrategy(
          this.logger,
          this.rtc,
          this.application
        );
        break;
    }
  }

  public propagateEvent(event: { id: string; type: TranscriptEvent }) {
    this.asrOfflineEventSubject.next(event);
  }

  public get eventReceived$(): Observable<{
    id: string;
    type: TranscriptEvent;
  }> {
    return this.asrOfflineEventSubject.asObservable();
  }

  public get query(): ApiQueryResource {
    return this.apiQuery;
  }

  public get cmd(): ApiCommandResource {
    return this.apiCommand;
  }

  public isFeatureSupported(feature: TranscriptTransformType): boolean {
    return this.impl.isFeatureSupported(feature);
  }

  public isDownloadFormattedTranscriptSupported(
    feature?: TranscriptTransformType
  ) {
    const provider = this.getConfiguredProvider(
      this.config.getApplicationConfig()
    );
    return [
      ApplicationProviderTypeEnum.OPEN_AI,
      ApplicationProviderTypeEnum.GOOGLE_AI_STUDIO,
      ApplicationProviderTypeEnum.GOOGLE_CLOUD,
    ].includes(provider);
  }

  public init(
    applicationId: string,
    conferenceId: string,
    handlers: ITranscriptEventHandlers
  ): void {
    this.instance = new Asr(this.common, {
      assetsPath: "/assets/asr/",
      applicationId,
      conferenceId,
    });
    this.instance.events.on(
      "ready",
      this.zone.run(() => handlers.ready)
    );
    this.instance.events.on(
      "reconnecting",
      this.zone.run(() => handlers.reconnecting)
    );
    this.instance.events.on(
      "reconnected",
      this.zone.run(() => handlers.reconnected)
    );
    this.instance.events.on("transcript", (e: ISpeechToTextTranscriptChunk) => {
      this.zone.run(() => {
        this._chunk.next(e);
        handlers.transcript?.(e);
      });
    });
    this.instance.events.on(
      "transcriptFailed",
      this.zone.run(() => handlers.transcriptFailed)
    );
    this.instance.events.on(
      "translationFailed",
      this.zone.run(() => handlers.translationFailed)
    );
  }

  /**
   * wait for 'ready' event, possible 'reconnecting' too
   *
   * @param organizationLanguageId  string
   * @param record boolean | optional. Record transcript or not. Storage provider must exist
   * @param translate boolean | optional. Translate transcript or not
   */
  public async enable(
    organizationLanguageId: string,
    record?: boolean,
    translate?: boolean
  ): Promise<void> {
    try {
      await this.instance.enable(organizationLanguageId, translate, record);
      this._enabled.next();
    } catch (ex) {
      throw ex;
    }
  }

  public disable(notify = true): Promise<void> {
    if (notify) {
      this._reset.next();
      this._disabled.next();
    }
    return this.instance?.disable();
  }

  public get isEnabled() {
    return this.instance?.isEnabled();
  }

  public get isInitialised() {
    return !!this.instance;
  }

  /**
   * Changes stream set for transcription.
   * If null or no audio tracks included, transcription is paused and should be called again with valid stream to resume.
   * if mediastream changes, set it again, with small interruptions.
   *
   * @param stream MediaStream
   * @returns boolean
   */
  public async setStream(stream: MediaStream): Promise<boolean> {
    return this.instance.setStream(stream);
  }

  public async destroy(conferenceId?: string) {
    await this.disable();
    if (!this.instance) {
      return;
    }
    this.instance.events.clear();
    this.instance = undefined;
    if (conferenceId) {
      // destroys transcriptId
      this.cmd.cleanUserConfiguration(conferenceId);
    }
  }

  /** strategy implementations */

  public removeOrganizationLanguage(
    provider: ApplicationProviderTypeEnum,
    language: ProviderOrganizationLanguage
  ) {
    return this.impl.removeProvisionedLanguage(provider, language);
  }

  public provisionLanguage(
    language: IProviderLanguage,
    options: IProvisionLanguageOptions
  ): Promise<{ organizationLanguageId: string }> {
    return this.impl.provisionLanguage(language, options);
  }

  public createTranscriptRequest(
    conversationId: string,
    organizationLanguageId: string
  ): Promise<{ conversationId: string; id: string }> {
    return this.impl.createTranscriptRequest(
      conversationId,
      organizationLanguageId
    );
  }

  public removeTranscript(conversationId: string, transcriptId: string) {
    return this.impl.removeTranscript(conversationId, transcriptId);
  }

  public transformTranscriptForConversation(
    conversationId: string,
    transcriptId: string,
    options: ITranscriptTransformOptions
  ) {
    return this.impl.transformTranscriptForConversation(
      conversationId,
      transcriptId,
      options
    );
  }

  public getTransformsForConversation(
    conversationId: string,
    transcriptId: string
  ): Promise<ITranscriptTransformStatus[]> {
    return this.impl.getTransformsForConversation(conversationId, transcriptId);
  }

  public getTransformStatusForConversation(
    conversationId: string,
    transcriptId: string,
    transformId: string
  ): Promise<ITranscriptTransformStatus> {
    return this.impl.getTransformStatusForConversation(
      conversationId,
      transcriptId,
      transformId
    );
  }

  getTransformURL(
    conversationId: string,
    transcriptId: string,
    transfromId: string
  ): Promise<{ url: string; validUntil: string }> {
    return this.impl.getTransformURL(conversationId, transcriptId, transfromId);
  }

  getTransformContent(
    conversationId: string,
    transcriptId: string,
    translationId: string
  ): Promise<ITransformContent> {
    return this.impl.getTransformContent(
      conversationId,
      transcriptId,
      translationId
    );
  }

  removeTransform(
    conversationId: string,
    transcriptId: string,
    transformId: string
  ): Promise<void> {
    return this.impl.removeTransform(conversationId, transcriptId, transformId);
  }

  public async getProviderLanguages(
    usage: "translations" | "transcriptions",
    page: number,
    pageSize: number
  ): Promise<PagedCollection<IProviderLanguages>> {
    return this.impl.getProviderLanguages(usage, page, pageSize);
  }

  public getTranscriptsForConversation(
    conversationId: string
  ): Promise<ITranscriptStatus[]> {
    return this.impl.getTranscriptsForConversation(conversationId);
  }

  getTranscriptStatusForConversation(
    conversationId: string,
    transcriptId: string
  ): Promise<ITranscriptStatus> {
    return this.impl.getTranscriptStatusForConversation(
      conversationId,
      transcriptId
    );
  }

  public async getOrganizationLanguages(
    page: number,
    pageSize: number
  ): Promise<PagedCollection<IOrganizationLanguage>> {
    return this.impl.getProvisionedLanguages(page, pageSize);
  }

  public getTranscriptURL(
    conversationId: string,
    transcriptId: string,
    type: "inline" | "attachment"
  ): Promise<{ url: string; validUntil: string }> {
    return this.impl.getTranscriptURL(conversationId, transcriptId, type);
  }

  public getTranscript(
    conversationId: string,
    transcriptId: string
  ): Promise<ITranscript> {
    return this.impl.getTranscript(conversationId, transcriptId);
  }

  getProvisionedLanguages(
    page: number,
    pageSize: number
  ): Promise<PagedCollection<IOrganizationLanguage>> {
    return this.impl.getProvisionedLanguages(page, pageSize);
  }
  removeProvisionedLanguage(
    provider: ApplicationProviderTypeEnum,
    language: ProviderOrganizationLanguage
  ) {
    return this.impl.removeProvisionedLanguage(provider, language);
  }
  getProviderModels(
    providerType: ApplicationProviderTypeEnum,
    region?: string,
  ): Promise<{
    models: IProviderModel[];
    defaults: Record<AIModelUsage, IProviderModelDefault>;
    tools?: LiveToolDefinition[];
    regions: string[];
    voices: string[];
    speechLanguages?: SpeechLanguage[];
  }> {
    return this.impl.getProviderModels(providerType, region);
  }
  getProvisionedModel(): Promise<IProvisionedModel[]> {
    return this.impl.getProvisionedModel();
  }
  provisionModel(
    provider: ApplicationProviderTypeEnum,
    model: IProvisionedModel
  ): Promise<{ organizationModelId: string }> {
    return this.impl.provisionModel(provider, model);
  }
  removeProvisionedModel(
    provider: ApplicationProviderTypeEnum,
    model: IProvisionedModel
  ): Promise<void> {
    return this.impl.removeProvisionedModel(provider, model);
  }

  translate(
    conversationId: string,
    transcriptId: string,
    options: ITranscriptTranslateOptions
  ): Promise<{ conversationId: string; id: string }> {
    return this.impl.translate(conversationId, transcriptId, options);
  }
  getTranslations(
    conversationId: string,
    transcriptId: string
  ): Promise<ITranscriptTranslateStatus[]> {
    return this.impl.getTranslations(conversationId, transcriptId);
  }
  getTranslation(
    conversationId: string,
    transcriptId: string,
    translationId: string
  ): Promise<ITranscriptTranslateStatus> {
    return this.impl.getTranslation(
      conversationId,
      transcriptId,
      translationId
    );
  }
  removeTranslation(
    conversationId: string,
    transcriptId: string,
    translationId: string
  ): Promise<void> {
    return this.impl.removeTranslation(
      conversationId,
      transcriptId,
      translationId
    );
  }
  getTranslationURL(
    conversationId: string,
    transcriptId: string,
    translationId: string
  ): Promise<{ url: string; validUntil: string }> {
    return this.impl.getTranslationURL(
      conversationId,
      transcriptId,
      translationId
    );
  }
  getTranslationContent(
    conversationId: string,
    transcriptId: string,
    translationId: string
  ): Promise<ITranscriptMessage[]> {
    return this.impl.getTranslationContent(
      conversationId,
      transcriptId,
      translationId
    );
  }
}
